import {
  useQuery,
  getFlaggedArticles,
  clearArticleFlags,
  hideArticle,
} from "wasp/client/operations";
import { useState, useEffect } from "react";
import { Button, SearchField, SurfaceCard } from "../../../components/ui";
import { AdminPaginationControl } from "../AdminPaginationControl";
import { ADMIN_DASHBOARD_PAGE_SIZE } from "../adminControlContracts";
import LoadingSpinner from "../../layout/LoadingSpinner";
import type { FlaggedArticleRow } from "../../operations";

const getSentimentColor = (sentiment: number) => {
  if (sentiment >= 4) return "text-success-600";
  if (sentiment >= 2) return "text-warning-600";
  return "text-danger-600";
};

const FlaggedArticlesTable = () => {
  const [page, setPage] = useState(1);
  const skip = (page - 1) * ADMIN_DASHBOARD_PAGE_SIZE;
  const [search, setSearch] = useState<string>("");
  const [selectedArticles, setSelectedArticles] = useState<number[]>([]);

  const normalizedSearch = search.trim();

  const { data, isLoading, error, refetch } = useQuery(getFlaggedArticles, {
    skip,
    limit: ADMIN_DASHBOARD_PAGE_SIZE,
    search: normalizedSearch || undefined,
  });

  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleClearFlags = async (articleId: number) => {
    try {
      await clearArticleFlags({ articleId });
      refetch();
    } catch (err) {
      console.error("Failed to clear flags:", err);
    }
  };

  const handleHideArticle = async (articleId: number) => {
    try {
      await hideArticle({ articleId });
      refetch();
    } catch (err) {
      console.error("Failed to hide article:", err);
    }
  };

  const toggleArticleSelection = (articleId: number) => {
    setSelectedArticles((prev) =>
      prev.includes(articleId)
        ? prev.filter((id) => id !== articleId)
        : [...prev, articleId],
    );
  };

  const toggleSelectAll = () => {
    if (!data?.articles) return;
    if (selectedArticles.length === data.articles.length) {
      setSelectedArticles([]);
    } else {
      setSelectedArticles(data.articles.map((a) => a.id));
    }
  };

  const totalPages = data?.totalPages || 1;

  if (error) {
    return (
      <SurfaceCard>
        <p className="text-danger-600">
          Failed to load flagged articles: {error.message}
        </p>
      </SurfaceCard>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <SurfaceCard padding="none">
        <div className="flex flex-col items-start gap-3 bg-neutral-100/40 p-4 dark:bg-neutral-700/50 sm:p-5">
          <span className="text-sm font-medium">Filters</span>
          <div className="flex w-full flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid w-full gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div>
                <label
                  htmlFor="flagged-search-filter"
                  className="mb-2 block text-sm font-medium text-text-primary dark:text-bodydark"
                >
                  Search
                </label>
                <SearchField
                  id="flagged-search-filter"
                  value={search}
                  onChange={setSearch}
                  placeholder="Search title or summary..."
                  className="w-full"
                  inputClassName="h-10 rounded-sm border border-stroke bg-white py-2 pl-10 pr-3 dark:border-form-strokedark dark:bg-form-input"
                />
              </div>
            </div>

            {!isLoading && (
              <div className="flex w-full flex-wrap items-center justify-start gap-3 xl:w-auto xl:justify-end">
                <AdminPaginationControl
                  page={page}
                  totalPages={totalPages}
                  onPageChange={(nextPage) => setPage(nextPage)}
                  className="justify-start xl:justify-end"
                />
              </div>
            )}
          </div>
        </div>

        <div className="hidden border-t border-stroke px-4 py-3 dark:border-strokedark md:grid md:grid-cols-12 md:px-6">
          <div className="col-span-1 flex items-center">
            <input
              type="checkbox"
              checked={
                data?.articles
                  ? selectedArticles.length === data.articles.length &&
                    data.articles.length > 0
                  : false
              }
              onChange={toggleSelectAll}
              className="rounded-sm border-stroke"
            />
          </div>
          <div className="col-span-4 flex items-center">
            <p className="font-medium">Title</p>
          </div>
          <div className="col-span-2 flex items-center">
            <p className="font-medium">Provider</p>
          </div>
          <div className="col-span-1 flex items-center">
            <p className="font-medium">Sentiment</p>
          </div>
          <div className="col-span-1 flex items-center">
            <p className="font-medium">Flags</p>
          </div>
          <div className="col-span-2 flex items-center">
            <p className="font-medium">Reasons</p>
          </div>
          <div className="col-span-1 flex items-center">
            <p className="font-medium">Actions</p>
          </div>
        </div>

        {isLoading && (
          <div className="-mt-40">
            <LoadingSpinner />
          </div>
        )}

        {!!data?.articles &&
          data.articles.length > 0 &&
          data.articles.map((article: FlaggedArticleRow) => (
            <div
              key={article.id}
              className="grid grid-cols-1 gap-2 border-t border-stroke px-4 py-2.5 dark:border-strokedark md:grid-cols-12 md:gap-2 md:px-6"
            >
              <div className="flex items-center justify-between md:col-span-1 md:justify-start">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Select
                </p>
                <input
                  type="checkbox"
                  checked={selectedArticles.includes(article.id)}
                  onChange={() => toggleArticleSelection(article.id)}
                  className="rounded-sm border-stroke"
                />
              </div>

              <div className="flex flex-col gap-1 md:col-span-4 md:justify-center">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Title
                </p>
                <div className="flex flex-col gap-0.5">
                  <p className="line-clamp-2 text-sm font-medium text-black dark:text-white">
                    {article.title}
                  </p>
                  <p className="line-clamp-1 text-xs text-neutral-600 dark:text-neutral-400">
                    {article.summary}
                  </p>
                  <a
                    href={article.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-info hover:text-info-800 dark:text-info-400"
                  >
                    Source
                  </a>
                </div>
              </div>

              <div className="flex flex-col gap-1 md:col-span-2 md:justify-center">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Provider
                </p>
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm text-black dark:text-white">
                    {article.provider}
                  </p>
                  <p className="text-xs text-neutral-600 dark:text-neutral-400">
                    {article.feedName}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between md:col-span-1 md:justify-start">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Sentiment
                </p>
                <span
                  className={`text-sm font-semibold ${getSentimentColor(
                    article.sentiment,
                  )}`}
                >
                  {article.sentiment}
                </span>
              </div>

              <div className="flex items-center justify-between md:col-span-1 md:justify-start">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Flags
                </p>
                <span className="inline-flex items-center rounded-full bg-warning/15 px-2 py-0.5 text-xs font-semibold text-warning-700 dark:text-warning-400">
                  {article.flagCount}
                </span>
              </div>

              <div className="flex flex-col gap-1 md:col-span-2 md:justify-center">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Reasons
                </p>
                <div className="flex flex-wrap gap-1">
                  {article.flagReasons.map((reason, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300"
                    >
                      {reason}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between md:col-span-1 md:justify-start">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 md:hidden">
                  Actions
                </p>
                <div className="flex flex-col gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    tone="neutral"
                    size="xs"
                    className="rounded-sm px-2 py-1"
                    onClick={() => handleClearFlags(article.id)}
                  >
                    Clear flags
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    tone="danger"
                    size="xs"
                    className="rounded-sm px-2 py-1"
                    onClick={() => handleHideArticle(article.id)}
                    disabled={article.isAdminHidden}
                  >
                    {article.isAdminHidden ? "Hidden" : "Hide permanently"}
                  </Button>
                </div>
              </div>
            </div>
          ))}

        {!isLoading && (!data?.articles || data.articles.length === 0) && (
          <div className="border-t border-stroke px-4 py-6 text-center dark:border-strokedark md:px-6">
            <p className="text-neutral-600 dark:text-neutral-400">
              No flagged articles found.
            </p>
          </div>
        )}

        {!isLoading && data && (
          <div className="border-t border-stroke px-6 py-3 dark:border-strokedark">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Showing {data.articles.length} of {data.totalCount} flagged
              articles
            </p>
          </div>
        )}
      </SurfaceCard>
    </div>
  );
};

export default FlaggedArticlesTable;
